import { Link } from '@tanstack/react-router';
import { ExternalLink, ListChecks, PauseCircle, Undo2, X } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import {
  EDUCATION_LEVEL_LABELS,
  MANAGER_RESPONSE_LABELS,
  REVIEW_STATUS_LABELS,
  REVIEW_WHO_ALL,
  applicationSourceLabel,
  reasonsForStages,
  type IcpRanked,
  type JobDetail,
  type RankedBy,
  type RankingRow,
  type ReviewPerson,
  type ReviewStatus,
  type ReviewWho,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { DataTable } from '../../../components/data-table/DataTable';
import { sortRows, type SortReaders } from '../../../components/data-table/sort-rows';
import type { Column, SortSpec } from '../../../components/data-table/types';
import { documentUrl } from '../../../components/cv/pdf-cache';
import { SearchInput } from '../../../components/Input';
import { Popover } from '../../../components/Popover';
import { Segmented } from '../../../components/Segmented';
import { Skeleton } from '../../../components/Skeleton';
import { Stars } from '../../../components/Stars';
import { MANAGER_RESPONSE_TONES, REVIEW_STATUS_TONES } from '../../../components/status-tones';
import { EmptyState } from '../../../components/system-states/SystemState';
import { toneCardClass } from '../../../components/Tone';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { EMPTY, formatDate, formatNumber } from '../../../lib/format';
import { useModule } from '../../../lib/modules';
import { EMPTY_SELECTION, isSelected, type SelectionState } from '../../../lib/selection';
import { useMediaQuery } from '../../../lib/use-media-query';
import { useCanEdit, useCurrentSession } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useCvBankAccess } from '../../cv-bank/hooks/use-cv-bank-actions';
import { usePipelineOptions } from '../../pipeline/api/pipeline-api';
import { validatePipelineSearch, type PipelineSearch } from '../../pipeline/pipeline-search';
import { sheetPlaceOf } from '../../ranking/ranking-view';
import { useProfileRanks } from '../../ranking/components/icp/use-profile-ranks';
import { WhyThisScore } from '../../ranking/components/WhyThisScore';
import { ReasonPicker } from '../../sorting/components/ReasonPicker';
import { useRejectWithReason, useReviewMove, useReviewPeople } from '../api/shortlist-api';
import { reviewListRowsOf } from '../review-list-rows';
import styles from './ReviewList.module.css';

// D6 (workflow review 2026-10-29, ADR 0080) — ④ Review as a list, like a sheet: everyone in review with the ideal candidate match, Jev's
// fit and a CV that opens in a new tab. HR reviews first (Shortlist, Maybe, Reject); the hiring manager's own review is their answer on
// SHL-01, shown here beside HR's. "All" and one "Reviewed by" per reviewer, by name, filter by who reviewed (evening review item 8). The board is the other layout.

/** A phone gets a card per person instead of the table, so nothing scrolls sideways (CLAUDE.md §5). */
const PHONE_MEDIA = '(max-width: 640px)';
/** Cards are drawn this many at a time; the table is virtualised and needs no limit. */
const CARDS_PER_PAGE = 50;
/** The server takes this many review moves in one go (`reviewMoveSchema`). */
const MOVE_LIMIT = 200;
const MONTHS_PER_YEAR = 12;
const NO_PEOPLE: ReadonlyMap<string, ReviewPerson> = new Map();

type Decision = ReviewStatus | 'rejected';

interface ReviewListProps {
  readonly detail: Pick<JobDetail, 'id' | 'workMode' | 'salaryBudget'> & Partial<Pick<JobDetail, 'requestedByUserId' | 'lineManagerUserId'>>;
  readonly stored: readonly RankingRow[];
  readonly rankedBy: RankedBy;
  readonly search: PipelineSearch;
  readonly onSearch: (next: PipelineSearch) => void;
  /** The List · Board switch, drawn at the end of the toolbar. */
  readonly switcher: ReactNode;
}

export function ReviewList({ detail, stored, rankedBy, search, onSearch, switcher }: ReviewListProps): ReactNode {
  const jobId = detail.id;
  const { rankedRows, ranks, items } = useProfileRanks({ jobId, job: detail, rows: stored, rankedBy });
  const people = useReviewPeople(jobId);
  const peopleById = useMemo(() => (people.data ? new Map(people.data.people.map((person) => [person.applicationId, person])) : NO_PEOPLE), [people.data]);
  const myId = useCurrentSession()?.id ?? '';
  const canDecide = useCanEdit('screening_decision');
  const { move, pending: moving } = useReviewMove(jobId);
  const rejection = useRejectWithReason(jobId);
  const options = usePipelineOptions(canDecide);
  const reasons = useMemo(() => reasonsForStages(options.data?.reasons ?? [], ['screening']), [options.data?.reasons]);
  const isPoolsOn = useModule('pools');
  // ADR 0082: the manager's answer column goes while "Shortlist to the line manager" is off.
  const isManagerStepOn = useModule('manager_shortlist');
  const mayPool = useCanEdit('talent_pool');
  const mayAddToCvBank = useCvBankAccess().canEdit;
  const show = useToasts((toasts) => toasts.show);
  const isPhone = useMediaQuery(PHONE_MEDIA);
  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const [rejecting, setRejecting] = useState<{ readonly rows: readonly RankingRow[]; readonly anchor: AnchorRect } | null>(null);
  const [cardsShown, setCardsShown] = useState(CARDS_PER_PAGE);

  const who: ReviewWho = search.rwho ?? REVIEW_WHO_ALL;
  const query = (search.q ?? '').trim().toLowerCase();
  const listed = useMemo(() => reviewListRowsOf(rankedRows, peopleById, who, query), [rankedRows, peopleById, who, query]);
  const reviewerName = (userId: string, name: string): string => (userId === myId ? `${name} (you)` : name);
  const chosenReviewer = listed.reviewers.find((reviewer) => reviewer.userId === who);
  const whoOptions = [
    { value: REVIEW_WHO_ALL, label: 'All', count: listed.total },
    ...listed.reviewers.map((reviewer) => ({ value: reviewer.userId, label: `Reviewed by ${reviewerName(reviewer.userId, reviewer.name)}`, count: reviewer.count })),
    // A reviewer whose people have all moved on stays picked, with 0, until someone picks another.
    ...(who !== REVIEW_WHO_ALL && !chosenReviewer ? [{ value: who, label: 'Reviewed by this person', count: 0 }] : []),
  ];
  const readers = useMemo(() => sortReadersOf(ranks, peopleById), [ranks, peopleById]);
  const rows = useMemo(() => sortRows(listed.rows, sort, readers), [listed.rows, sort, readers]);
  const selected = rows.filter((row) => isSelected(selection, row.applicationId));
  const isBusy = (id: string): boolean => moving.has(id) || rejection.pending.has(id);
  const update = (patch: Partial<PipelineSearch>): void => onSearch(validatePipelineSearch({ ...search, ...patch }));
  const open = (applicationId: string): void => update({ app: applicationId });

  if (!stored.some((row) => row.stage === 'screening' || row.stage === 'new')) {
    return (
      <div className={styles.stage}>
        <div className={styles.toolbar}>
          <span className={styles.end}>{switcher}</span>
        </div>
        <EmptyState
          title="Nobody to review yet"
          body="Import a spreadsheet of applicants on ② Import. Each one is ranked, then lands here To review."
          action={
            <Link to="/jobs/$jobId/$tab" params={{ jobId, tab: 'import' }} className={styles.action}>
              Import a CSV
            </Link>
          }
          isInset
        />
      </div>
    );
  }

  /** One decision for these people: moves go at once with Undo (F23.08); a rejection asks for the reason first (#2). */
  function decide(targets: readonly RankingRow[], to: Decision, anchor: AnchorRect | null): void {
    const movable = targets.filter((row) => refusalOf(row) === null);
    if (movable.length === 0) {
      const [first] = targets;
      show({ tone: 'info', message: 'Nobody selected can move from here', detail: first ? (refusalOf(first) ?? undefined) : undefined });
      return;
    }
    if (movable.length > MOVE_LIMIT) {
      show({ tone: 'warning', message: `At most ${MOVE_LIMIT} at a time`, detail: 'Select fewer people and try again.' });
      return;
    }
    if (to === 'rejected') {
      if (anchor) setRejecting({ rows: movable, anchor });
      return;
    }
    const items = movable.filter((row) => (row.reviewStatus ?? 'to_review') !== to).map((row) => ({ applicationId: row.applicationId, name: row.name, from: row.reviewStatus ?? 'to_review' }));
    setSelection(EMPTY_SELECTION);
    void move(items, to);
  }

  /** a shortlist · h maybe · r reject on the focused row, as on the board and Ranking (06-qol §2). */
  function onRowKey(row: RankingRow, key: string, rowElement: HTMLElement | null): boolean {
    if (!canDecide || refusalOf(row) !== null) return false;
    const to = ROW_KEYS[key];
    if (!to || !rowElement) return false;
    if (to === (row.reviewStatus ?? 'to_review')) return false;
    decide([row], to, anchorFrom(rowElement));
    return true;
  }

  const allColumns = columnsOf({ ranks, people: peopleById, isPeopleLoading: people.isPending, isBusy, open });
  const columns = isManagerStepOn ? allColumns : allColumns.filter((column) => column.id !== 'manager');
  const place = sheetPlaceOf(rows, search.app);
  const openedRow = search.app ? rankedRows.find((row) => row.applicationId === search.app) : undefined;
  const askId = openedRow?.profileReading?.askId ?? null;
  const emptyState = (
    <EmptyState
      title={query !== '' ? `Nobody matches “${search.q?.trim() ?? ''}”` : who === myId ? 'You haven’t reviewed anyone here yet' : 'Nobody here was reviewed by this person'}
      body={query !== '' ? 'Check the spelling, or search by city.' : 'People someone shortlists, sets to Maybe, or answers Yes or No for show under their name.'}
      action={
        <Button variant="outline" onClick={() => update({ q: undefined, rwho: undefined })}>
          Show everyone
        </Button>
      }
      isInset
    />
  );

  return (
    <div className={styles.stage}>
      <div className={styles.toolbar}>
        <span className={styles.search}>
          <SearchInput
            aria-label="Search this review by name or city"
            placeholder="Search by name or city"
            value={search.q ?? ''}
            onChange={(event) => update({ q: event.target.value === '' ? undefined : event.target.value })}
            onClear={() => update({ q: undefined })}
            // `/` focuses the primary search on any list screen (06-qol §2).
            data-primary-search
          />
        </span>
        <Segmented<string>
          label="Whose review"
          value={who}
          options={whoOptions}
          onChange={(value) => {
            setSelection(EMPTY_SELECTION);
            update({ rwho: value === REVIEW_WHO_ALL ? undefined : value });
          }}
        />
        <span className={styles.end}>{switcher}</span>
        <p className={styles.hint}>{hintOf(chosenReviewer ? reviewerName(chosenReviewer.userId, chosenReviewer.name) : null, rows.length, canDecide)}</p>
        {people.error && (
          <p className={styles.notice} role="status">
            Who reviewed whom couldn’t be loaded.{' '}
            <Button size="sm" variant="ghost" onClick={() => void people.refetch()}>
              Try again
            </Button>
          </p>
        )}
      </div>

      {isPhone ? (
        <ul className={styles.cards} aria-label="People in review">
          {rows.length === 0 && <li>{emptyState}</li>}
          {rows.slice(0, cardsShown).map((row) => (
            <ReviewCardRow
              key={row.applicationId}
              row={row}
              rank={ranks?.get(row.applicationId)}
              person={peopleById.get(row.applicationId)}
              isBusy={isBusy(row.applicationId)}
              canDecide={canDecide && refusalOf(row) === null}
              onOpen={() => open(row.applicationId)}
              onDecide={(to, trigger) => decide([row], to, anchorFrom(trigger))}
            />
          ))}
          {rows.length > cardsShown && (
            <li className={styles.more}>
              <Button variant="outline" block onClick={() => setCardsShown((count) => count + CARDS_PER_PAGE)}>
                Show {formatNumber(Math.min(CARDS_PER_PAGE, rows.length - cardsShown))} more of {formatNumber(rows.length - cardsShown)}
              </Button>
            </li>
          )}
        </ul>
      ) : (
        <div className={styles.table}>
          <DataTable
            screenId="review-list"
            label="People in review"
            columns={columns}
            rows={rows}
            getRowId={(row) => row.applicationId}
            sort={sort}
            onSortChange={setSort}
            selection={selection}
            onSelectionChange={setSelection}
            isSelectable={canDecide}
            onOpenRow={(row) => open(row.applicationId)}
            onRowKey={onRowKey}
            toneOf={(row) => REVIEW_STATUS_TONES[row.reviewStatus ?? 'to_review']}
            emptyState={emptyState}
            rowActions={canDecide ? (row) => <RowDecisions row={row} isBusy={isBusy(row.applicationId)} isCompact onDecide={(to, trigger) => decide([row], to, anchorFrom(trigger))} /> : undefined}
            bulkActions={
              <>
                <Button size="sm" variant="outline" icon={ListChecks} onClick={(event) => decide(selected, 'shortlisted', anchorFrom(event.currentTarget))}>
                  Shortlist
                </Button>
                <Button size="sm" variant="outline" icon={PauseCircle} onClick={(event) => decide(selected, 'maybe', anchorFrom(event.currentTarget))}>
                  Maybe
                </Button>
                <Button size="sm" variant="outline" icon={Undo2} onClick={(event) => decide(selected, 'to_review', anchorFrom(event.currentTarget))}>
                  Back to To review
                </Button>
                <Button size="sm" variant="danger" icon={X} onClick={(event) => decide(selected, 'rejected', anchorFrom(event.currentTarget))}>
                  Reject…
                </Button>
              </>
            }
          />
        </div>
      )}
      <p className={styles.footnote}>People rejected in review are under Out on the stage bar, and in the board’s Rejected lane, where they can be brought back.</p>

      <Popover anchor={rejecting?.anchor ?? null} onClose={() => setRejecting(null)} label="Reject" align="left">
        {rejecting && (
          <ReasonPicker
            key={rejecting.rows.map((row) => row.applicationId).join(',')}
            mode="reject"
            reasons={reasons}
            pools={options.data?.pools ?? []}
            canPool={isPoolsOn && mayPool}
            contactAttempts={Math.min(...rejecting.rows.map((row) => row.contactAttempts))}
            candidateName={rejecting.rows.length === 1 && rejecting.rows[0] ? rejecting.rows[0].name : `${rejecting.rows.length} people`}
            canAddToCvBank={mayAddToCvBank && rejecting.rows.some((row) => row.cv !== 'none')}
            onConfirm={(choice) => {
              const targets = rejecting.rows.map((row) => ({ applicationId: row.applicationId, name: row.name }));
              setRejecting(null);
              setSelection(EMPTY_SELECTION);
              void rejection.reject(targets, choice.reasonCode, { poolIds: choice.poolIds, newPoolNames: choice.newPoolNames, addToCvBank: choice.addToCvBank });
            }}
          />
        )}
      </Popover>

      <WhyThisScore
        applicationId={search.app ?? null}
        jobId={jobId}
        onClose={() => update({ app: undefined })}
        review={canDecide && openedRow && refusalOf(openedRow) === null ? { row: openedRow, isMoving: isBusy(openedRow.applicationId), onShortlist: () => decide([openedRow], 'shortlisted', null) } : undefined}
        icp={ranks && openedRow ? { items, ranked: ranks.get(openedRow.applicationId), penalty: openedRow.knockoutPenalty, place: null, ask: askId === null ? null : { id: askId } } : undefined}
        steps={
          place && {
            position: place.position,
            total: place.total,
            onPrevious: place.previousId === null ? null : () => update({ app: place.previousId ?? undefined }),
            onNext: place.nextId === null ? null : () => update({ app: place.nextId ?? undefined }),
          }
        }
      />
    </div>
  );
}

const ROW_KEYS: Readonly<Record<string, Decision>> = { a: 'shortlisted', h: 'maybe', r: 'rejected' };

function hintOf(reviewer: string | null, count: number, canDecide: boolean): string {
  const people = `${formatNumber(count)} ${count === 1 ? 'person' : 'people'}`;
  const keys = canDecide ? ' Tick people to act on many, or on a row press A shortlist · H maybe · R reject.' : '';
  if (reviewer) return `${people} reviewed by ${reviewer}.${keys}`;
  return `${people} in review, what waits for HR first.${keys}`;
}

/** Why this person can't be moved from the list, in the server's words, or null (§L25). */
export function refusalOf(row: RankingRow): string | null {
  if (row.stage !== 'screening') return 'They are still at New. Review them in the review card.';
  if (row.reviewStatus === 'shortlisted' && row.managerResponse !== null && row.managerResponse !== 'not_sent') return 'Already sent to the manager — answer it on ⑤ Shortlist.';
  return null;
}

// --- Columns ------------------------------------------------------------------------------------

interface ColumnsInput {
  readonly ranks: ReadonlyMap<string, IcpRanked> | undefined;
  readonly people: ReadonlyMap<string, ReviewPerson>;
  readonly isPeopleLoading: boolean;
  readonly isBusy: (applicationId: string) => boolean;
  readonly open: (applicationId: string) => void;
}

export function columnsOf({ ranks, people, isPeopleLoading, isBusy, open }: ColumnsInput): readonly Column<RankingRow>[] {
  const later = (cell: ReactNode): ReactNode => (isPeopleLoading ? <Skeleton width="60%" /> : cell);
  return [
    {
      id: 'name',
      header: 'Name',
      isIdentity: true,
      isSortable: true,
      defaultWidth: 240,
      exportValue: (row) => row.name,
      cell: (row) => (
        <span className={styles.nameCell}>
          <button type="button" className={styles.nameButton} onClick={() => open(row.applicationId)}>
            {row.name || EMPTY}
          </button>
          {row.missingMustHaves.length > 0 && <Chip tone="rejected" plain>Missing: {row.missingMustHaves.join(', ')}</Chip>}
        </span>
      ),
    },
    {
      id: 'match',
      header: 'Match',
      headerTitle: 'Match with the ideal candidate',
      isSortable: true,
      isNumeric: true,
      defaultWidth: 136,
      exportValue: (row) => row.score,
      cell: (row) => <MatchCell row={row} />,
    },
    {
      id: 'fit',
      header: 'Jev fit',
      headerTitle: 'How strong the evidence is, where the CV says something (fit), and how much of the profile it covers',
      isSortable: true,
      isNumeric: true,
      defaultWidth: 112,
      exportValue: (row) => ranks?.get(row.applicationId)?.fit ?? null,
      cell: (row) => <FitCell rank={ranks?.get(row.applicationId)} />,
    },
    { id: 'hr', header: 'HR ★', isSortable: true, isNumeric: true, defaultWidth: 80, exportValue: (row) => row.hrStars, cell: (row) => (row.hrStars === null ? EMPTY : row.hrStars.toFixed(1)) },
    {
      id: 'status',
      header: 'HR’s review',
      isSortable: true,
      defaultWidth: 132,
      exportValue: (row) => REVIEW_STATUS_LABELS[row.reviewStatus ?? 'to_review'],
      cell: (row) => <StatusChip row={row} isBusy={isBusy(row.applicationId)} />,
    },
    {
      id: 'reviewer',
      header: 'Reviewed by',
      isSortable: true,
      defaultWidth: 160,
      exportValue: (row) => people.get(row.applicationId)?.reviewedBy?.name ?? null,
      cell: (row) => later(<ActorCell actor={people.get(row.applicationId)?.reviewedBy ?? null} />),
    },
    {
      id: 'manager',
      header: 'Manager’s review',
      headerTitle: 'The hiring manager’s own review of the people HR passed, on their shortlist',
      isSortable: true,
      defaultWidth: 176,
      exportValue: (row) => (row.reviewStatus === 'shortlisted' ? MANAGER_RESPONSE_LABELS[row.managerResponse ?? 'not_sent'] : null),
      cell: (row) => <ManagerCell row={row} person={people.get(row.applicationId)} />,
    },
    { id: 'experience', header: 'Experience', isSortable: true, isNumeric: true, defaultWidth: 104, exportValue: (row) => yearsOf(row), cell: (row) => yearsOf(row) },
    { id: 'city', header: 'City', isSortable: true, defaultWidth: 132, exportValue: (row) => row.city, cell: (row) => row.city || EMPTY },
    { id: 'education', header: 'Education', defaultWidth: 132, exportValue: (row) => (row.education ? EDUCATION_LEVEL_LABELS[row.education] : null), cell: (row) => (row.education ? EDUCATION_LEVEL_LABELS[row.education] : EMPTY) },
    { id: 'source', header: 'Source', isSortable: true, defaultWidth: 132, exportValue: (row) => sourceOf(row), cell: (row) => sourceOf(row) || EMPTY },
    {
      id: 'cv',
      header: 'CV',
      defaultWidth: 120,
      exportValue: (row) => people.get(row.applicationId)?.cv?.fileName ?? null,
      cell: (row) => later(<CvLink row={row} person={people.get(row.applicationId)} />),
    },
  ];
}

const UNSORTED_LAST = -1;

/** How each sortable column reads a row; an empty value sorts below every real one. */
function sortReadersOf(ranks: ReadonlyMap<string, IcpRanked> | undefined, people: ReadonlyMap<string, ReviewPerson>): SortReaders<RankingRow> {
  return {
    name: (row) => row.name,
    match: (row) => row.score ?? UNSORTED_LAST,
    fit: (row) => ranks?.get(row.applicationId)?.fit ?? UNSORTED_LAST,
    hr: (row) => row.hrStars ?? UNSORTED_LAST,
    status: (row) => REVIEW_STATUS_LABELS[row.reviewStatus ?? 'to_review'],
    reviewer: (row) => people.get(row.applicationId)?.reviewedBy?.name ?? '',
    manager: (row) => (row.reviewStatus === 'shortlisted' ? MANAGER_RESPONSE_LABELS[row.managerResponse ?? 'not_sent'] : ''),
    experience: (row) => row.experienceMonths ?? UNSORTED_LAST,
    city: (row) => row.city ?? '',
    source: (row) => sourceOf(row),
  };
}

function yearsOf(row: RankingRow): string {
  return row.experienceMonths === null ? EMPTY : `${(row.experienceMonths / MONTHS_PER_YEAR).toFixed(1)} yrs`;
}

// --- Cells --------------------------------------------------------------------------------------

/** The ideal candidate match over its stars; one dash for someone not ranked yet. */
function MatchCell({ row }: { readonly row: RankingRow }): ReactNode {
  if (row.score === null) return <span className={styles.muted}>{EMPTY}</span>;
  return (
    <span className={styles.match}>
      <span className={styles.number}>{row.score}</span>
      <Stars value={row.stars} />
    </span>
  );
}

/** ADR 0058 §A: fit is worked out when read, never stored; someone with nothing read has none. */
function FitCell({ rank }: { readonly rank: IcpRanked | undefined }): ReactNode {
  if (!rank || rank.fit === null) return <span className={styles.muted}>{EMPTY}</span>;
  return (
    <span className={styles.fit} title={`Fit ${rank.fit} · covers ${rank.covers}% of the profile`}>
      <span className={styles.number}>{rank.fit}</span>
      <span className={styles.sub}>covers {rank.covers}%</span>
    </span>
  );
}

function StatusChip({ row, isBusy }: { readonly row: RankingRow; readonly isBusy: boolean }): ReactNode {
  const status = row.reviewStatus ?? 'to_review';
  return (
    <Chip tone={REVIEW_STATUS_TONES[status]} plain>
      {isBusy ? 'Saving…' : REVIEW_STATUS_LABELS[status]}
    </Chip>
  );
}

function ActorCell({ actor }: { readonly actor: ReviewPerson['reviewedBy'] }): ReactNode {
  if (!actor) return <span className={styles.muted}>{EMPTY}</span>;
  return (
    <span className={styles.actor} title={formatDate(actor.at)}>
      {actor.name ?? 'A former user'}
    </span>
  );
}

/** The second review: only HR's picks have one, and it is answered on SHL-01. */
function ManagerCell({ row, person }: { readonly row: RankingRow; readonly person: ReviewPerson | undefined }): ReactNode {
  if (row.reviewStatus !== 'shortlisted') return <span className={styles.muted}>{EMPTY}</span>;
  const answer = row.managerResponse ?? 'not_sent';
  const by = person?.managerReviewedBy ?? null;
  return (
    <span className={styles.manager}>
      <Chip tone={MANAGER_RESPONSE_TONES[answer]} plain={answer !== 'awaiting'}>
        {MANAGER_RESPONSE_LABELS[answer]}
      </Chip>
      {by && (
        <span className={styles.sub}>
          {by.name ?? 'A former user'}
          {by.isOnBehalf ? ' (HR)' : ''}
        </span>
      )}
    </span>
  );
}

/** The CV, in a new tab, through the permission-checked file link (D6). Keyboard reachable as any link. */
function CvLink({ row, person }: { readonly row: RankingRow; readonly person: ReviewPerson | undefined }): ReactNode {
  const cv = person?.cv ?? null;
  if (cv && cv.isRemoved) return <span className={styles.muted}>Removed</span>;
  if (cv) {
    return (
      <a className={styles.cvLink} href={documentUrl(cv.documentId)} target="_blank" rel="noopener noreferrer" aria-label={`Open ${row.name}'s CV in a new tab`} title={cv.fileName}>
        <ExternalLink size={14} aria-hidden />
        Open CV
      </a>
    );
  }
  return <span className={styles.muted}>{row.cv === 'coming' ? 'CV coming' : 'No CV'}</span>;
}

/**
 * The decisions that apply from where the person is now. In the table they are icons revealed on hover (the row stays one line, and
 * the keys do the same); on a phone's card they carry their words.
 */
function RowDecisions({
  row,
  isBusy,
  isCompact = false,
  onDecide,
}: {
  readonly row: RankingRow;
  readonly isBusy: boolean;
  readonly isCompact?: boolean;
  readonly onDecide: (to: Decision, trigger: HTMLElement) => void;
}): ReactNode {
  if (refusalOf(row) !== null) return null;
  const status = row.reviewStatus ?? 'to_review';
  const words = (label: string): string | undefined => (isCompact ? undefined : label);
  return (
    <span className={isCompact ? styles.rowIcons : styles.rowActions}>
      {status !== 'shortlisted' && (
        <Button size="sm" variant="ghost" icon={ListChecks} disabled={isBusy} aria-label={`Shortlist ${row.name}`} title={isCompact ? 'Shortlist (A)' : undefined} onClick={(event) => onDecide('shortlisted', event.currentTarget)}>
          {words('Shortlist')}
        </Button>
      )}
      {status !== 'maybe' && (
        <Button size="sm" variant="ghost" icon={PauseCircle} disabled={isBusy} aria-label={`Maybe for ${row.name}`} title={isCompact ? 'Maybe (H)' : undefined} onClick={(event) => onDecide('maybe', event.currentTarget)}>
          {words('Maybe')}
        </Button>
      )}
      <Button size="sm" variant="ghost" icon={X} disabled={isBusy} aria-label={`Reject ${row.name}`} title={isCompact ? 'Reject… (R)' : undefined} onClick={(event) => onDecide('rejected', event.currentTarget)}>
        {words('Reject…')}
      </Button>
    </span>
  );
}

/** A phone's row: the same facts, stacked, and the decisions as buttons (no hover on a phone). */
function ReviewCardRow({
  row,
  rank,
  person,
  isBusy,
  canDecide,
  onOpen,
  onDecide,
}: {
  readonly row: RankingRow;
  readonly rank: IcpRanked | undefined;
  readonly person: ReviewPerson | undefined;
  readonly isBusy: boolean;
  readonly canDecide: boolean;
  readonly onOpen: () => void;
  readonly onDecide: (to: Decision, trigger: HTMLElement) => void;
}): ReactNode {
  return (
    <li className={`${toneCardClass()} ${styles.card}`} data-tone={REVIEW_STATUS_TONES[row.reviewStatus ?? 'to_review']}>
      <span className={styles.cardHead}>
        <button type="button" className={styles.nameButton} onClick={onOpen}>
          {row.name || EMPTY}
        </button>
        <StatusChip row={row} isBusy={isBusy} />
      </span>
      <span className={styles.cardFacts}>
        <span>
          Match <MatchCell row={row} />
        </span>
        <span>Jev fit {rank?.fit ?? EMPTY}</span>
        <span>HR ★ {row.hrStars === null ? EMPTY : row.hrStars.toFixed(1)}</span>
      </span>
      <span className={styles.cardFacts}>
        <span>Reviewed by {person?.reviewedBy?.name ?? EMPTY}</span>
        {row.reviewStatus === 'shortlisted' && <ManagerCell row={row} person={person} />}
      </span>
      <span className={styles.cardFacts}>
        <span>{[yearsOf(row), row.city, sourceOf(row)].filter((part) => part && part !== EMPTY).join(' · ') || EMPTY}</span>
        <CvLink row={row} person={person} />
      </span>
      {canDecide && <RowDecisions row={row} isBusy={isBusy} onDecide={onDecide} />}
    </li>
  );
}

/** The source in words ("Walk-in", the board's name), never its stored key (evening review item 14). */
function sourceOf(row: RankingRow): string {
  return row.source ? applicationSourceLabel(row.source) : '';
}
